import { formatDateTime, type ResolvedField } from '@tnn/core'
import { currentLocale } from '@tnn/i18n'
import { type ReactNode, type RefObject, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useReactToPrint } from 'react-to-print'
import { type Rec, resourceApi } from '../api/resources'
import { useOptionalSession } from '../auth/session'
import { type FieldValueContext, fieldText } from '../resource/FieldValue'
import classes from './print.module.css'

const NUMERIC = new Set(['number', 'integer', 'money', 'percent'])

export interface PrintOptions {
  landscape?: boolean
  pageLabel?: string
  /**
   * `receipt`: an 80 mm roll; the printer driver sets the length and the page has no margins or page numbers.
   * `a5`: half an A4 (landscape is A4's width), with less margin above and below
   */
  paper?: 'a4' | 'a5' | 'receipt'
  /**
   * `narrow`: A4 with a thin margin all round, room left below for the page numbers (a long list: ใบคุมรถ).
   * `form`: a one-page form that fills the paper (50 ทวิ): the same thin margin below too, no page numbers or stamp
   */
  margins?: 'normal' | 'narrow' | 'form'
  /** Printed when and by whom: in the page's bottom-left margin (a roll has none: its document draws `usePrint`'s) */
  stamp?: string
  /** What the printout is, atop every page but the first (`PrintRunning`) */
  running?: Running
}

/** A printout's line atop its pages after the first: `start` on the left, `end` on the right */
export interface Running {
  start: string
  end?: string
}

const cssString = (text: string) => `"${text.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`
const marginFont = 'font: 8pt "Google Sans Variable", "Noto Sans Thai", sans-serif; color: #555;'
const runningBox = `${marginFont} vertical-align: bottom; padding-bottom: 2mm;`

/**
 * Page setup shared by every printout; Chrome prints the page numbers, the stamp beside them, and from the second page
 * the running line above. A row never splits across two pages.
 */
export function pageStyle({ landscape = false, pageLabel = 'Page', paper = 'a4', margins = 'normal', stamp, running }: PrintOptions = {}) {
  if (paper === 'receipt') {
    return `
      @page { margin: 0; }
      html, body { background: #fff !important; width: 80mm; margin: 0; }
    `
  }
  const a4 = { normal: '12mm 12mm 14mm', narrow: '6mm 6mm 10mm', form: '6mm' }[margins]
  const [size, margin] = paper === 'a5' ? ['A5', '8mm 12mm 10mm'] : ['A4', a4]
  const footer =
    margins === 'form'
      ? ''
      : `@bottom-right { content: "${pageLabel} " counter(page) " / " counter(pages); ${marginFont} }
      ${stamp ? `@bottom-left { content: ${cssString(stamp)}; ${marginFont} }` : ''}`
  // The first page has the printout's own head; the pages after keep 10 mm above for the line, inside what printers reach
  const top = Number.parseFloat(margin)
  const header =
    running && margins !== 'form'
      ? `@page { ${top < 10 ? 'margin-top: 10mm;' : ''}
        @top-left { content: ${cssString(running.start)}; ${runningBox} }
        ${running.end ? `@top-right { content: ${cssString(running.end)}; ${runningBox} }` : ''} }
      @page :first { ${top < 10 ? `margin-top: ${top}mm;` : ''} @top-left { content: none; } @top-right { content: none; } }`
      : ''
  return `
    @page { size: ${size} ${landscape ? 'landscape' : 'portrait'}; margin: ${margin};
      ${footer} }
    ${header}
    html, body { background: #fff !important; }
    tr { break-inside: avoid; }
  `
}

/**
 * What a printout is, atop every page after the first, for a sheet that comes apart from the rest: its parts join
 * with " · ", `start` on the left and `end` on the right. Anywhere in what prints; `usePrint` reads it as it prints.
 */
export function PrintRunning({ start, end }: { start: (string | null | undefined)[]; end?: (string | null | undefined)[] }) {
  const join = (parts?: (string | null | undefined)[]) => parts?.filter(Boolean).join(' · ') || undefined
  return <span hidden data-running-start={join(start)} data-running-end={join(end)} />
}

/** What a printout says of itself: when, by whom, and of a record the how-manyth print it is */
export interface PrintStamp {
  text: string
  /** Of a record (`usePrint`'s `record`): 1 the first time; missing when it could not be counted */
  count?: number
}

/**
 * Prints `ref`'s content, stamped with when and by whom ("พิมพ์เมื่อ … โดย …"), and from the second page with its
 * `PrintRunning` line. Of a `record`, the print is logged in its activity first and the stamp says the how-manyth it is
 * from the second on, so a copy reads as one. The stamp is drawn before the print starts: `print` sets it, and the
 * render that shows it prints.
 */
export function usePrint(opts: PrintOptions & { title: string; record?: { resource: string; id: unknown } }) {
  const ref = useRef<HTMLDivElement>(null)
  const { t, i18n } = useTranslation()
  const session = useOptionalSession()
  const [job, setJob] = useState<{ stamp: PrintStamp; running?: Running } | null>(null)
  const stamp = job?.stamp ?? null
  const reactToPrint = useReactToPrint({
    contentRef: ref,
    documentTitle: opts.title,
    pageStyle: pageStyle({ ...opts, stamp: stamp?.text, running: job?.running }),
  })
  const pending = useRef(false)
  useEffect(() => {
    if (!job || !pending.current) return
    pending.current = false
    reactToPrint()
  }, [job, reactToPrint])
  const { resource, id } = opts.record ?? {}
  const name = session?.me.user.name
  const print = useCallback(async () => {
    // A print not logged (offline, a page anyone opens) still prints, without its count
    const count =
      resource && id && session
        ? await resourceApi(resource)
            .printed(String(id))
            .then((reply) => reply.count)
            .catch(() => undefined)
        : undefined
    const at = formatDateTime(new Date(), { locale: currentLocale(i18n) })
    const text = name ? t('print.printedBy', { at, name }) : t('print.printedAt', { at })
    const mark = ref.current?.querySelector<HTMLElement>('[data-running-start]')?.dataset
    pending.current = true
    setJob({
      stamp: { text: count && count > 1 ? `${text} · ${t('print.time', { count })}` : text, count },
      running: mark?.runningStart ? { start: mark.runningStart, end: mark.runningEnd } : undefined,
    })
  }, [resource, id, session, name, i18n, t])
  return { ref, print, stamp }
}

/** On a roll receipt, which has no margins: when and by whom it was printed, and that it is a reprint */
export function PrintStampLine({ stamp, className }: { stamp: PrintStamp | null; className?: string }) {
  if (!stamp) return null
  return <div className={className ?? classes.stamp}>{stamp.text}</div>
}

/** A printout shown on screen as it prints (a document a customer opens on a link); `printRef` prints it */
export function PrintSheet({ printRef, children }: { printRef?: RefObject<HTMLDivElement | null>; children: ReactNode }) {
  return (
    <div ref={printRef} className={classes.doc}>
      {children}
    </div>
  )
}

/** Content that only exists for printing; hidden on screen. */
export function PrintArea({ printRef, children }: { printRef: RefObject<HTMLDivElement | null>; children: ReactNode }) {
  return (
    <div style={{ display: 'none' }}>
      <div ref={printRef} className={classes.doc}>
        {children}
      </div>
    </div>
  )
}

export interface PrintableListProps {
  title: string
  subtitle?: string
  fields: ResolvedField[]
  records: Rec[]
  ctx: FieldValueContext
}

/** A printout's title and what it covers, atop the pages after too; when and by whom it was printed is in the margin (`usePrint`) */
function PrintHead({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <header className={classes.head}>
      <PrintRunning start={[title]} end={[subtitle]} />
      <div>
        <h1 className={classes.title}>{title}</h1>
        {subtitle && <div className={classes.sub}>{subtitle}</div>}
      </div>
    </header>
  )
}

/** A list as a clean A4 table: header row repeats on every page; when it was printed is in the margin (`usePrint`). */
export function PrintableList({ title, subtitle, fields, records, ctx }: PrintableListProps) {
  return (
    <>
      <PrintHead title={title} subtitle={subtitle} />
      <table className={classes.table}>
        <thead>
          <tr>
            {fields.map((f) => (
              <th key={f.name} data-num={NUMERIC.has(f.type) || undefined}>
                {ctx.t(f.label)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {records.map((r) => (
            <tr key={r.id}>
              {fields.map((f) => (
                <td key={f.name} data-num={NUMERIC.has(f.type) || undefined}>
                  {fieldText(f, r[f.name], ctx)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </>
  )
}

export interface PrintableTableProps {
  title: string
  subtitle?: string
  /** `num`: a figure, set at the end of its cell */
  columns: { header: string; num?: boolean }[]
  rows: string[][]
  /** The last row, in bold: the totals */
  foot?: string[]
}

/** Rows already in words (a report's) as the same A4 table as a list */
export function PrintableTable({ title, subtitle, columns, rows, foot }: PrintableTableProps) {
  const cells = (row: string[]) => row.map((cell, i) => ({ cell, num: columns[i]?.num || undefined, key: columns[i]?.header ?? i }))
  return (
    <>
      <PrintHead title={title} subtitle={subtitle} />
      <table className={classes.table}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.header} data-num={c.num || undefined}>
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <PrintRows
          rows={rows.map((row, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: a printout's rows stay in their report's order
            <tr key={i}>
              {cells(row).map((c) => (
                <td key={c.key} data-num={c.num}>
                  {c.cell}
                </td>
              ))}
            </tr>
          ))}
          close={
            foot && (
              <tr className={classes.total}>
                {cells(foot).map((c) => (
                  <td key={c.key} data-num={c.num}>
                    {c.cell}
                  </td>
                ))}
              </tr>
            )
          }
        />
      </table>
    </>
  )
}

/**
 * A printed table's rows and the rows that close it (totals, signatures): the close prints once, under the last row and
 * on its page, never alone atop a page (Chrome repeats a `tfoot` on every page, so the close is not one)
 */
export function PrintRows({ rows, close }: { rows: ReactNode[]; close?: ReactNode }) {
  if (!close) return <tbody>{rows}</tbody>
  return (
    <>
      {rows.length > 1 && <tbody>{rows.slice(0, -1)}</tbody>}
      <tbody className={classes.end}>
        {rows.at(-1)}
        {close}
      </tbody>
    </>
  )
}
